import { useMutation } from "@apollo/client/react";
import { useCurrentUser, usePremiumFeature } from "@hooks";
import { Tooltip } from "@mui/material";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AccountItemLine } from "./AccountItem";
import EDIT_USER from "../../graphql/mutations/EditUser";
import { alert } from "../common/Alert";
import {
  DSButton,
  DSInput,
  DSLinkButton,
} from "../design-system/molecules";

export default function ProfileSettings() {
  const navigate = useNavigate();

  const { user } = useCurrentUser();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();

  const shouldShowChangeEmail = hasFeatureAccess(
    UserPermissions.CHANGE_EMAIL_ENABLED,
  );

  return (
    <div className="flex-1 flex flex-col gap-md">
      <div className="flex-1 divide-y divide-secondary">
        <AccountItemLine title="Name:" dataTestId="profile-name-line">
          <ProfileNameSection user={user} />
        </AccountItemLine>
        <AccountItemLine
          title="Email:"
          dataTestId="profile-email-line"
        >
          <Tooltip
            arrow
            placement="right"
            title={
              user?.isConfirmed
                ? "You have successfully confirmed your email."
                : "You have not confirmed your email yet."
            }
          >
            <div className="flex flex-row items-center gap-sm">
              <span className="text-primary">{user?.email}</span>
              <div className="flex flex-row items-center gap-sm">
                {user?.isConfirmed ? (
                  <span className="text-success text-xs">✓</span>
                ) : (
                  <span className="text-danger text-xs">✗</span>
                )}
                {shouldShowChangeEmail && (
                  <DSLinkButton
                    dataTestId="manage-email-button"
                    onClick={() =>
                      navigate("/registration_confirmation")
                    }
                  >
                    Manage Email
                  </DSLinkButton>
                )}
              </div>
            </div>
          </Tooltip>
        </AccountItemLine>
        <AccountItemLine
          title="User ID:"
          dataTestId="profile-user-id-line"
        >
          <span className="text-primary">{user?.uuid}</span>
        </AccountItemLine>
      </div>
    </div>
  );
}

function ProfileNameSection({ user }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedLastName, setEditedLastName] = useState("");
  const [editUser] = useMutation(EDIT_USER);

  const onSaveClicked = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await editUser({
        variables: {
          firstName: editedFirstName,
          lastName: editedLastName,
        },
      });
      setIsEditing(false);

      alert("success", "Account updated successfully.");
    } catch (err) {
      mixpanel.track("Update Name Failed", {
        error: err.message || err,
      });
      alert("error", err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    const isValid =
      editedFirstName.length > 0 && editedLastName.length > 0;
    return (
      <form
        className="flex flex-col md:flex-row md:items-end gap-sm"
        onSubmit={onSaveClicked}
      >
        <DSInput
          type="text"
          placeholder="First name"
          maxLength={100}
          value={editedFirstName}
          onChange={(e) => setEditedFirstName(e.target.value)}
          disabled={isSaving}
          dataTestId="profile-first-name-input"
        />
        <DSInput
          type="text"
          placeholder="Last name"
          maxLength={100}
          value={editedLastName}
          onChange={(e) => setEditedLastName(e.target.value)}
          disabled={isSaving}
          dataTestId="profile-last-name-input"
        />
        <div className="flex gap-sm">
          <DSButton
            type="button"
            variant="cancel"
            size="sm"
            disabled={isSaving}
            onClick={() => setIsEditing(false)}
            dataTestId="profile-cancel-button"
          >
            Cancel
          </DSButton>
          <DSButton
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSaving || !isValid}
            dataTestId="profile-save-button"
          >
            Save
          </DSButton>
        </div>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-sm">
      <span className="text-primary">
        {user?.firstName} {user?.lastName}
      </span>
      <DSLinkButton
        dataTestId="profile-update-name-button"
        onClick={() => {
          setIsEditing(true);
          setEditedFirstName(user?.firstName || "");
          setEditedLastName(user?.lastName || "");
        }}
      >
        Update
      </DSLinkButton>
    </div>
  );
}
